import {Customer} from "../../models";

export interface DeepLinkHandlerArgs {
    url: string;
    customers: Customer[];
    setSelectedCustomerId: (arg: { id: number; }) => Promise<{ selectedId: any; }>;
    history: {
        push: (url: string) => void;
        replace?: (url: string) => void;
        location?: {
            search: string;
        };
    };
    ionRouter?: { push: (path: string, direction?: any, action?: any) => void };
    currentLocation?: string;
}

const APP_HOSTS = new Set([
    'app.swool.io',
    'localhost',
]);

const ROUTES_REQUIRING_CUSTOMER_CONTEXT = new Set([
    '/home',
    '/account',
    '/chat',
    '/my-shop',
    '/checkout',
    '/payment-return',
    '/notifications',
    '/settings',
    '/customer',
    '/document',
    '/slots',
    '/profile',
    '/events',
]);

const normalizePath = (pathname: string): string => {
    if (!pathname) return '/';
    if (pathname.length > 1 && pathname.endsWith('/')) {
        return pathname.slice(0, -1);
    }
    return pathname;
};

const getBaseRoute = (pathname: string): string => {
    if (pathname.startsWith('/document/')) return '/document';
    if (pathname.startsWith('/slots/')) return '/slots';
    if (pathname.startsWith('/profile/')) return '/profile';
    if (pathname.startsWith('/events/')) return '/events';
    return pathname;
};

const parseCustomerId = (value: string | null): string | number | null => {
    if (!value) return null;
    const parsed = Number(value);
    if (Number.isInteger(parsed) && parsed > 0) return parsed;
    // Return string if it's not a numeric ID (potentially a UUID)
    return value;
};

const normalizeUuid = (uuid: any): string => {
    if (typeof uuid !== 'string') return String(uuid || '');
    const low = uuid.toLowerCase();
    if (low.length === 32 && !low.includes('-')) {
        return low.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, "$1-$2-$3-$4-$5");
    }
    return low;
};

const isAllowedHost = (host: string): boolean => {
    return APP_HOSTS.has(host);
};

const isAllowedPath = (pathname: string): boolean => {
    const allowedExact = new Set([
        '/',
        '/home',
        '/account',
        '/customer',
        '/customer-select',
        '/chat',
        '/my-shop',
        '/checkout',
        '/payment-return',
        '/notifications',
        '/settings',
        '/auth/login',
        '/auth/forgot',
        '/auth/request-verify',
        '/auth/verify',
    ]);

    if (allowedExact.has(pathname)) return true;
    if (pathname.startsWith('/document/')) return true;
    if (pathname.startsWith('/slots/')) return true;
    if (pathname.startsWith('/profile/')) return true;
    if (pathname.startsWith('/events/')) return true;

    return false;
};

export const requiresCustomerContext = (url: string): boolean => {
    try {
        const normalizedUrl = url.replace(/&amp;/g, '&');
        const parsedUrl = new URL(normalizedUrl);
        const pathname = normalizePath(parsedUrl.pathname);
        const baseRoute = getBaseRoute(pathname);
        const customerId = parseCustomerId(parsedUrl.searchParams.get('customerId') || parsedUrl.searchParams.get('customer_id'));
        return !!(ROUTES_REQUIRING_CUSTOMER_CONTEXT.has(baseRoute) && customerId);
    } catch {
        return false;
    }
};

export const getCustomerIdFromUrl = (url: string): string | number | null => {
    try {
        const normalizedUrl = url.replace(/&amp;/g, '&');
        const parsedUrl = new URL(normalizedUrl);
        return parseCustomerId(parsedUrl.searchParams.get('customerId') || parsedUrl.searchParams.get('customer_id'));
    } catch {
        return null;
    }
};

export const hasCustomerInList = (url: string, customers: Customer[]): boolean => {
    const customerId = getCustomerIdFromUrl(url);
    if (!customerId) return false;
    return !!customers?.find((c) =>
        typeof customerId === 'number'
            ? Number(c?.id) === customerId
            : normalizeUuid(c?.uuid) === normalizeUuid(customerId)
    );
};

export const handleDeepLinkUrl = async ({
                                            url,
                                            customers,
                                            setSelectedCustomerId,
                                            history,
                                            ionRouter,
                                            currentLocation,
                                        }: DeepLinkHandlerArgs): Promise<boolean> => {
    // A deep-link tap is an out-of-band event: the on-screen route is incidental,
    // not a real predecessor. Push through the Ionic router with direction 'none'
    // so it records no `pushedByRoute` — otherwise the target page's IonBackButton
    // returns to whatever happened to be showing (e.g. /customer-select) instead of
    // its own backRouterLink/defaultHref. Web has no Ionic router here and instead
    // hard-navigates (window.location), which reboots the stack fresh and is
    // unaffected — so fall back to history.push there.
    const navigate = (path: string) => (ionRouter ? ionRouter.push(path, 'none') : history.push(path));
    try {
        const normalizedUrl = url.replace(/&amp;/g, '&');
        const parsedUrl = new URL(normalizedUrl);
        const pathname = normalizePath(parsedUrl.pathname);
        const baseRoute = getBaseRoute(pathname);

        if (!isAllowedHost(parsedUrl.hostname)) {
            console.warn('Ignoring deep link for unexpected host:', parsedUrl.hostname, url);
            return false;
        }

        if (!isAllowedPath(pathname)) {
            console.warn('Ignoring unsupported deep link path:', pathname, url);
            return false;
        }

        const customerId = parseCustomerId(parsedUrl.searchParams.get('customerId') || parsedUrl.searchParams.get('customer_id'));

        if (ROUTES_REQUIRING_CUSTOMER_CONTEXT.has(baseRoute) && customerId) {
            const customer = customers?.find((c) =>
                typeof customerId === 'number'
                    ? Number(c?.id) === customerId
                    : normalizeUuid(c?.uuid) === normalizeUuid(customerId)
            );

            if (!customer) {
                console.warn('Deep link customer identifier was not found in loaded customers:', customerId);
                return false;
            }

            await setSelectedCustomerId({id: Number(customer.id)});
        }

        if (pathname === '/') {
            return false;
        }

        const targetUrl = pathname + parsedUrl.search;
        const currentSearch = history.location?.search || '';
        if (currentLocation !== pathname || parsedUrl.search !== currentSearch) {
            navigate(targetUrl);
        }

        return true;
    } catch (error) {
        console.error('Failed to handle deep link URL:', url, error);
        return false;
    }
};
